import type { FreshnessMeta } from "@/lib/api";

export type SoldCostsEntity = "LTD" | "OU" | "LLC";

export type PnlTieLine = { kind: string; account_name: string | null; amount: string; component?: string; tie_status?: string };

export type SoldCostTie = {
  entity_id: string; month: string; currency: string; reason: string | null; source: string | null; xero_reloading: boolean;
  xero_live: string | null; xero_closed: string | null; shown: string | null; lines: PnlTieLine[];
  unexplained: string | null; ties: boolean; typed_only: string[];
};

export type SoldCostsSelection = {
  xero_sku: string;
  channel_location: string;
  marketplace_id: string;
  entity_id?: SoldCostsEntity;
};

export type SoldCostsKey = SoldCostsSelection & {
  entity_id: string;
  product: string;
  tracking: string;
  variant: string;
  region: "UK" | "EU" | "MENA";
  channel: "Amazon" | "Shopify" | "Wholesale";
  location: string;
  marketplace_name: string | null;
  currency: string | null;
  row_kind: "unit" | "case";
  units_per_case: number;
};

export type SoldCostsMonth = {
  month: string;
  units: string;
  amount: string;
  per_unit: string | null;
  per_unit_trailing_3: string | null;
  change?: string | null;
  change_pct?: string | null;
  beyond_band?: boolean | null;
  closed: boolean;
  waiting_for_sheet?: boolean;
  history?: boolean;
};

export type SoldCostsHeld = { entity_id: string; month: string; reason: string };

export type SoldCostsLeaf = {
  node_id: string;
  leaf_label: string;
  ofs_group: string;
  ofs_group_label: string;
  amount: string;
  per_unit: string | null;
  per_unit_trailing_3: string | null;
  value_expected: string | null;
  expected_flag: string | null;
  cost_behaviour: string;
  treatment: string;
  parent_code?: string;
};

export type SoldCostsCovered = {
  node_id: string;
  leaf_label: string;
  ofs_group: string;
  ofs_group_label: string;
  ofs_amount: string;
  currency: string | null;
  status: "covered" | "no_units" | "product_cost_missing";
  waiting_for_sheet?: boolean;
};

export type SoldCostsDetailMonth = SoldCostsMonth & {
  version: number;
  leaves: SoldCostsLeaf[];
  covered: SoldCostsCovered[];
};

export type SoldCostsList = {
  filters: { locations: string[]; products: string[]; variants: string[] };
  floor: string;
  from: string;
  to: string;
  keys: Array<SoldCostsKey & { months: SoldCostsMonth[] }>;
  page: number;
  pages: number;
  total: number;
  held?: SoldCostsHeld[];
};

export type SoldCostsDetail = { key: SoldCostsKey; months: SoldCostsDetailMonth[]; held?: SoldCostsHeld[]; entities?: string[] };
export type SoldCostsView = "fee" | "change" | "pct";
export type SoldCostsHistoryParams = {
  from: string;
  to: string;
  q?: string;
  product?: string;
  variant?: string;
  region?: string;
  channel?: string;
  location?: string;
  page?: number;
  view?: SoldCostsView;
  sort?: string;
  dir?: "asc" | "desc";
};
export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export type SoldCostMonthRow = {
  entity_id: "LTD" | "OU" | "LLC";
  month: string;
  version: number | null;
  closed: boolean;
  run_status: string | null;
  run_reason: string | null;
  visible: boolean;
  pool: string | null;
  covered: string | null;
  units_in_scope: number | null;
  avg_per_unit: string | null;
  currency: string | null;
  units_excluded?: number | null;
  waiting_for_sheet?: boolean;
  history?: boolean;
  held?: boolean;
  held_reason?: string | null;
};

export type ProductCostParity = {
  item_total: string | null;
  allocated: string | null;
  unallocated: string | null;
  units_gap: string | null;
  account_total: string;
  residual: string | null;
  residual_pct: string | null;
  no_account_pool: boolean;
  product_cost_missing?: boolean;
};

export type SoldCostProduct = {
  tracking: string;
  units: number;
  product_amount: string;
  per_unit: string;
  weight_basis: string;
  item_name?: string;
  qty?: string;
  product_amount_gbp?: string;
  per_unit_gbp?: string;
  per_unit_local?: string;
  local_currency?: string;
  source_modified_at?: string;
};

export type SoldCostMonthLeaf = {
  node_id: string;
  leaf_label: string;
  ofs_group: string;
  treatment: string;
  status: string;
  ofs_amount: string;
  allocated_amount: string | null;
  products: SoldCostProduct[];
  waiting_for_sheet?: boolean;
};

export type SoldCostRun = {
  run_id: string;
  status: string;
  reason: string | null;
  written_at: string;
  detail: {
    parity?: ProductCostParity;
    product_cost_load_id?: string;
    product_cost_stale?: boolean;
    item_without_units?: string[];
    case_without_registry?: string[];
    product_cost_missing?: string[];
  };
};

export type SoldCostMonth = {
  entity_id: string;
  month: string;
  version: number;
  closed: boolean;
  run: SoldCostRun | null;
  latest_run: SoldCostRun | null;
  leaves: SoldCostMonthLeaf[];
  currency: string | null;
  history?: boolean;
};

export type ProductCostLoad = {
  load_id: string;
  rows: number;
  skipped: number;
  source_modified_at: string;
  detail: { case_rows_reviewed?: string[] } & Record<string, unknown>;
};

type ErrorBody = {
  error?: string;
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  detail?: string | Array<{ msg?: string }>;
};

export class SoldCostsError extends Error {
  readonly code: string | null;

  constructor(readonly status: number, readonly body: ErrorBody | null) {
    super(`Sold Costs API ${status}`);
    this.code = body?.error ?? body?.code ?? null;
  }
}

const BASE = import.meta.env.VITE_PFC_API_BASE ?? "/fulfilment_costs/v1";
const JSON_HEADERS = { Accept: "application/json", "Content-Type": "application/json" };

async function safeBody(response: Response): Promise<ErrorBody | null> {
  try { return await response.json() as ErrorBody; }
  catch { return null; }
}

async function raw(url: string, init: RequestInit): Promise<Response> {
  let response: Response;
  try { response = await fetch(url, { credentials: "include", ...init }); }
  catch { throw new SoldCostsError(0, null); }
  if (!response.ok) throw new SoldCostsError(response.status, await safeBody(response));
  return response;
}

async function request<T>(path: string): Promise<Enveloped<T>> {
  const response = await raw(`${BASE}${path}`, { method: "GET", headers: JSON_HEADERS });
  return await response.json() as Enveloped<T>;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value != null && value !== "") query.set(key, String(value));
  const text = query.toString();
  return text ? `?${text}` : "";
}

export function getSoldCostsHistory(params: SoldCostsHistoryParams): Promise<Enveloped<SoldCostsList>> {
  return request(`/sold-costs/history${qs(params)}`);
}

export function getSoldCostsDetail(
  selection: SoldCostsSelection,
  window?: { from: string; to: string },
): Promise<Enveloped<SoldCostsDetail>> {
  return request(`/sold-costs/history${qs({ ...window, ...selection })}`);
}

export const getSoldCostMonths = (entity: string) =>
  request<SoldCostMonthRow[]>(`/sold-costs/months${qs({ entity_id: entity })}`);

export const getSoldCostMonth = (entity: string, month: string) =>
  request<SoldCostMonth>(`/sold-costs/months/${entity}/${month}`);

export const getSoldCostMonthTie = (entity: string, month: string) =>
  request<SoldCostTie>(`/sold-costs/months/${entity}/${month}/tie`);

export async function postProductCostLoad(): Promise<Enveloped<ProductCostLoad>> {
  const response = await raw(`${BASE}/sold-costs/product-cost/load`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: "{}",
  });
  return await response.json() as Enveloped<ProductCostLoad>;
}
